/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useId, useRef, useState } from 'react';
import { FONTE_TEXTO } from '../paleta';

/**
 * Multi-select de GRUPOS da tabela de bots (aba Dependências) — substitui o select
 * único de Grupo mantendo a linguagem visual do protótipo (`sistema/index.html`):
 * o botão tem a mesma cara do select roxo da tabela (borda/fundo/chevron ⌄ roxos,
 * linhas 596-603 do protótipo) e mostra PÍLULAS dos grupos selecionados (mesmo formato
 * das badges de 20px de raio do protótipo) ou "Sem grupo"; o painel lista um checkbox
 * por grupo criado; clique fora fecha. Sem lib nova — só o CSS escopado `.orq-proto`.
 *
 * Cada toggle chama `onToggle(grupoId)` — o CHAMADOR monta a lista completa e dispara
 * o PATCH (o backend substitui `grupo_ids` inteiro; erro 400 aparece no card).
 */

export interface OpcaoGrupo {
  id: string;
  nome: string;
}

interface MultiSelectGruposProps {
  /** grupo_ids atuais do bot (ids reais uuid4 hex — nunca sentinelas de UI). */
  selecionados: string[];
  /** Grupos criados (bot_organizacao) — as opções do painel. */
  opcoes: OpcaoGrupo[];
  // eslint-disable-next-line no-unused-vars
  onToggle: (grupoId: string) => void;
}

export const MultiSelectGrupos: React.FC<MultiSelectGruposProps> = ({
  selecionados,
  opcoes,
  onToggle,
}) => {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const idBase = useId();

  // Clique fora fecha — mesmo gesto dos dropdowns do protótipo.
  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (ev: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(ev.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const setSel = new Set(selecionados);
  const gruposSelecionados = opcoes.filter(g => setSel.has(g.id));

  return (
    <div ref={raiz} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <div
        onClick={() => setAberto(atual => !atual)}
        title="Grupos deste bot — clique para editar"
        style={{
          width: '100%',
          minHeight: '17px',
          padding: '6px 30px 6px 8px',
          borderRadius: '8px',
          border: '1px solid oklch(0.85 0.03 295)',
          background: 'oklch(0.97 0.012 295)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px',
          cursor: 'pointer',
          fontFamily: FONTE_TEXTO,
        }}
      >
        {gruposSelecionados.length === 0 && (
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'oklch(0.5 0.05 295)' }}>
            Sem grupo
          </span>
        )}
        {gruposSelecionados.map(g => (
          <span
            key={g.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '2px 8px',
              borderRadius: '20px',
              background: 'oklch(0.94 0.025 295)',
              color: 'oklch(0.32 0.09 295)',
              fontSize: '10.5px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                background: 'oklch(0.5 0.12 295)',
                flex: 'none',
              }}
            />
            {g.nome}
          </span>
        ))}
        <span
          style={{
            position: 'absolute',
            right: '10px',
            pointerEvents: 'none',
            fontSize: '10px',
            color: 'oklch(0.4 0.09 295)',
          }}
        >
          ⌄
        </span>
      </div>

      {aberto && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: '100%',
            zIndex: 40,
            background: 'oklch(1 0 0)',
            border: '1px solid oklch(0.9 0.006 160)',
            borderRadius: '10px',
            boxShadow: '0 12px 34px oklch(0.2 0.02 165 / 0.18)',
            padding: '6px',
            maxHeight: '240px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {opcoes.length === 0 && (
            <div
              style={{
                padding: '8px 10px',
                fontSize: '12px',
                color: 'oklch(0.55 0.015 165)',
                fontFamily: FONTE_TEXTO,
              }}
            >
              Nenhum grupo criado ainda — crie no card &quot;Grupos de bots&quot;.
            </div>
          )}
          {opcoes.map(g => (
            <label
              key={g.id}
              htmlFor={`${idBase}-${g.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 10px',
                borderRadius: '7px',
                cursor: 'pointer',
                fontFamily: FONTE_TEXTO,
                fontSize: '12.5px',
                fontWeight: 600,
                color: 'oklch(0.3 0.02 165)',
                background: setSel.has(g.id) ? 'oklch(0.97 0.012 295)' : 'transparent',
              }}
            >
              <input
                id={`${idBase}-${g.id}`}
                type="checkbox"
                checked={setSel.has(g.id)}
                onChange={() => onToggle(g.id)}
                style={{ accentColor: 'oklch(0.45 0.1 295)', cursor: 'pointer', margin: 0 }}
              />
              {g.nome}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
